import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";

const MarkupModal = ({ visaRequest, onSave, onClose }) => {
  const [markup, setMarkup] = useState(0);
  const [supplierPrice, setSupplierPrice] = useState(0);
  const [serviceFee, setServiceFee] = useState(0);
  const [customerPrice, setCustomerPrice] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (visaRequest) {
      // Use existing prices directly from backend
      
      const supplier = visaRequest.totalPrice-visaRequest.serviceFee  || 0;
      const currentMarkup = visaRequest.markUP || 0;
      const currentServiceFee = visaRequest.serviceFee || 0;
      const currentCustomerPrice = visaRequest.customerPrice || 0;
      const currentTotalPrice = visaRequest.totalPrice || 0;

      setSupplierPrice(supplier);
      setMarkup(currentMarkup);
      setServiceFee(currentServiceFee);
      setCustomerPrice(currentCustomerPrice);
      setTotalPrice(currentTotalPrice);
    }
  }, [visaRequest]);

  // Update customer price when markup changes
  const handleMarkupChange = (value) => {
    const newMarkup = parseFloat(value) || 0;
    setMarkup(newMarkup);

    const supplier = parseFloat(supplierPrice) || 0;
    const service = parseFloat(serviceFee) || 0;

    // Customer Price = Supplier Price + Service Fee + Markup
    const newCustomerPrice = supplier + service + newMarkup;

    setCustomerPrice(newCustomerPrice);
    // Total Price remains the same (Supplier Price + Service Fee)
  };

  // If customer price is manually edited, calculate markup from it
  const handleCustomerPriceChange = (value) => {
    const newCustomerPrice = parseFloat(value) || 0;
    setCustomerPrice(newCustomerPrice);

    const supplier = parseFloat(supplierPrice) || 0;
    const service = parseFloat(serviceFee) || 0;

    // Markup = Customer Price - Supplier Price - Service Fee
    const calculatedMarkup = newCustomerPrice - supplier - service;
    setMarkup(calculatedMarkup > 0 ? calculatedMarkup : 0);
  };

  const handleSave = async () => {
    if (!visaRequest) return;

    setIsLoading(true);
    try {
      const pricingData = {
        visaRequestId: visaRequest.visaRequestId,
        supplierPrice: parseFloat(supplierPrice) || 0,
        markUP: parseFloat(markup) || 0,
        serviceFee: parseFloat(serviceFee) || 0,
        customerPrice: parseFloat(customerPrice) || 0,
        totalPrice: parseFloat(totalPrice) || 0,
      };

      console.log("Sending pricing data to backend:", pricingData);

      const response = await apiService.put(
        config.UPDATEVISAREQUESTPRICING,
        pricingData
      );

      if (response) {
        onSave();
        onClose();
      } else {
        throw new Error("Failed to update pricing");
      }
    } catch (error) {
      console.error("Error saving pricing:", error);
      alert(`Error saving pricing: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-sm" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Update Pricing (INR)</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={isLoading}
            ></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">Supplier Price</label>
              <input
                type="number"
                className="form-control"
                value={supplierPrice}
                disabled
              />
              <small className="text-muted">Base price from supplier</small>
            </div>

            <div className="mb-3">
              <label className="form-label">Service Fee</label>
              <input
                type="number"
                className="form-control"
                value={serviceFee}
                disabled
              />
              <small className="text-muted">Service fee from backend</small>
            </div>

            <div className="mb-3">
              <label className="form-label">Markup Amount</label>
              <input
                type="number"
                className="form-control"
                value={markup}
                onChange={(e) => handleMarkupChange(e.target.value)}
                min="0"
                step="0.01"
                disabled={isLoading}
              />
              <small className="text-muted">Additional profit margin</small>
            </div>

            <div className="mb-3">
              <label className="form-label">Customer Price</label>
              <input
                type="number"
                className="form-control fw-bold"
                value={customerPrice}
                onChange={(e) => handleCustomerPriceChange(e.target.value)}
                min="0"
                step="0.01"
                disabled={isLoading}
              />
              <small className="text-muted">
                Supplier Price + Service Fee + Markup
              </small>
            </div>

            <div className="mb-3">
              <label className="form-label">Total Price</label>
              <input
                type="number"
                className="form-control fw-bold text-success"
                value={totalPrice}
                disabled
              />
              <small className="text-muted">Supplier Price + Service Fee</small>
            </div>

            {/* Price Breakdown Summary */}
            <div className="mt-3 p-3 bg-light rounded small">
              <h6 className="mb-2">Price Breakdown:</h6>
              <div className="d-flex justify-content-between mb-1">
                <span>Supplier Price:</span>
                <span>
                  INR {parseFloat(supplierPrice).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-1">
                <span>Service Fee:</span>
                <span>
                  INR {parseFloat(serviceFee).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-1">
                <span>Markup:</span>
                <span className="text-success">
                  + INR {parseFloat(markup).toLocaleString("en-IN")}
                </span>
              </div>
              <hr className="my-2" />
              <div className="d-flex justify-content-between fw-bold">
                <span>Customer Pays:</span>
                <span className="text-primary">
                  INR {parseFloat(customerPrice).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="d-flex justify-content-between fw-bold">
                <span>Total Amount:</span>
                <span className="text-success">
                  INR {parseFloat(totalPrice).toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-danger"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save Pricing"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarkupModal;
